import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  CheckIcon,
  ArrowSwitchIcon,
  CloudIcon,
  DiffIcon,
  RocketIcon,
  EyeIcon,
  LockIcon,
  ShieldIcon,
  SearchIcon,
  SparkleIcon,
  ProjectIcon,
  StarIcon,
  FlowchartIcon,
  XIcon,
  CodeIcon,
  HomeIcon
} from '@primer/octicons-react';

// --- Constants ---
// Moved sections outside component to avoid recreation
const SECTIONS = [
  { id: 'home', title: 'Home', full: 'Home', icon: <HomeIcon />, path: '/' },
  { id: 'validator', title: 'JSON Validator', full: 'Validator', icon: <CheckIcon />, path: '/validator' },
  { id: 'schema-validator', title: 'Schema Validator', full: 'Schema', icon: <ShieldIcon />, path: '/schema-validator' },
  { id: 'viewer', title: 'JSON Viewer', full: 'Viewer', icon: <EyeIcon />, path: '/viewer' },
  { id: 'inspector', title: 'JSON Inspector', full: 'Inspector', icon: <SearchIcon />, path: '/inspector' },
  { id: 'tree-visualizer', title: 'Tree Visualizer', full: 'Visualizer', icon: <ProjectIcon />, path: '/visualizer' },
  { id: 'score', title: 'JSON Score', full: 'Score', icon: <StarIcon />, path: '/json-score' },
  { id: 'converter', title: 'JSON Converter', full: 'Converter', icon: <ArrowSwitchIcon />, path: '/converter' },
  { id: 'security-scanner', title: 'Security Scanner', full: 'Scanner', icon: <ShieldIcon />, path: '/security-scanner' },
  { id: 'compare', title: 'JSON Compare', full: 'Compare', icon: <DiffIcon />, path: '/compare' },
  { id: 'encoder', title: 'Encode / Decode', full: 'Encode', icon: <LockIcon />, path: '/encoder' },
  { id: 'query', title: 'JSON Query', full: 'Query', icon: <SearchIcon />, path: '/query' },
  { id: 'api-tester', title: 'API Tester', full: 'API Tester', icon: <RocketIcon />, path: '/api-tester' },
  { id: 'generator', title: 'Data Generator', full: 'Generate', icon: <SparkleIcon />, path: '/generator' },
  { id: 'code-generator', title: 'Code Generator', full: 'CodeGen', icon: <CodeIcon />, path: '/code-generator' },
  { id: 'schema', title: 'JSON Schema', full: 'Schema', icon: <FlowchartIcon />, path: '/schema' },
  { id: 'masker', title: 'JSON Masker', full: 'Masker', icon: <ShieldIcon />, path: '/masker' },
  { id: 'hosting', title: 'JSON Hosting', full: 'Hosting', icon: <CloudIcon />, path: '/hosting' }
];

// Custom dark blue palette
const darkBlue = {
  bg: '#0a1929',
  bgLighter: '#112233',
  textPrimary: '#ffffff',
  textSecondary: '#e0e0e0',
  textTertiary: '#b0b0b0',
  border: 'rgba(255, 255, 255, 0.1)',
  accent: '#3b82f6',
  iconBg: 'rgba(255, 255, 255, 0.08)',
  iconHoverBg: 'rgba(59, 130, 246, 0.2)',
  iconActiveBg: 'rgba(59, 130, 246, 0.3)',
  tooltipBg: '#1e2a3a',
};

// --- Styled Components ---
const SidebarContainer = styled.aside`
  background: linear-gradient(180deg, ${darkBlue.bg} 0%, ${darkBlue.bgLighter} 100%);
  color: ${darkBlue.textSecondary};
  width: ${({ $sidebarState, $isMobile }) => {
    if ($isMobile) {
      return $sidebarState === 'expanded' ? '100%' : '0';
    }
    if ($sidebarState === 'expanded') return '240px';
    if ($sidebarState === 'collapsed') return '72px';
    return '0';
  }};
  height: ${({ $isMobile }) => $isMobile ? 'calc(100vh - 56px)' : 'calc(100vh - 64px)'};
  position: fixed;
  top: ${({ $isMobile }) => $isMobile ? '56px' : '64px'};
  left: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${darkBlue.border};
  box-shadow: ${({ $isMobile, $sidebarState }) =>
    $isMobile && $sidebarState === 'expanded' ? '0 0 30px rgba(0, 0, 0, 0.5)' : '2px 0 12px rgba(0, 0, 0, 0.1)'};
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: ${({ $sidebarState }) => $sidebarState === 'expanded' ? '100%' : '0'};
    opacity: ${({ $sidebarState }) => $sidebarState === 'expanded' ? '1' : '0'};
  }
`;

const SidebarHeader = styled.div`
  padding: ${({ $sidebarState, $isMobile }) =>
    $isMobile ? '0.75rem' :
      $sidebarState === 'collapsed' ? '0.875rem' : '0.875rem 1rem'};
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: ${({ $sidebarState, $isMobile, $isSearchActive }) => {
    if ($isMobile || $sidebarState === 'collapsed') return 'center';
    if ($isSearchActive) return 'space-between';
    return 'space-between';
  }};
  position: relative;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $sidebarState }) =>
    $sidebarState === 'collapsed' ? 'center' : 'flex-start'};
  gap: 12px;
  flex: ${({ $isSearchActive }) => ($isSearchActive ? '0' : '1')};
  min-width: 0;
  opacity: ${({ $isSearchActive, $sidebarState }) =>
    $isSearchActive ? '0' : '1'};
  transition: opacity 0.2s ease, flex 0.2s ease;
`;

const LogoImage = styled.div`
  width: ${({ $sidebarState, $isMobile }) =>
    $isMobile ? '28px' : ($sidebarState === 'collapsed' ? '24px' : '24px')};
  height: ${({ $sidebarState, $isMobile }) =>
    $isMobile ? '28px' : ($sidebarState === 'collapsed' ? '24px' : '24px')};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: ${({ $sidebarState }) =>
      $sidebarState === 'collapsed' ? 'brightness(1.2)' : 'brightness(1)'};
    transition: all 0.2s ease;
  }
`;

const SidebarTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${darkBlue.textPrimary};
  margin: 0;
  opacity: ${({ $sidebarState, $isMobile }) => {
    if ($isMobile) return '1';
    return $sidebarState === 'collapsed' ? '0' : '1';
  }};
  transition: opacity 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  display: ${({ $isSearchActive }) => $isSearchActive ? 'none' : 'block'};
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: ${({ $isSearchActive }) => $isSearchActive ? '1' : '0'};
  transition: flex 0.2s ease;
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid ${darkBlue.border};
  color: ${darkBlue.textSecondary};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  
  &:hover {
    background: ${darkBlue.iconBg};
    color: ${darkBlue.textPrimary};
    border-color: rgba(255, 255, 255, 0.25);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  display: ${({ $sidebarState, $isSearchActive }) =>
    $isSearchActive ? 'none' : ($sidebarState === 'collapsed' ? 'none' : 'flex')};
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  animation: slideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 7px 10px;
  border-radius: 6px;
  background: ${darkBlue.iconBg};
  border: 1px solid ${darkBlue.border};
  color: ${darkBlue.textPrimary};
  font-size: 13px;
  font-weight: 400;
  outline: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:focus {
    background: rgba(255, 255, 255, 0.1);
    border-color: ${darkBlue.accent};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: ${darkBlue.textTertiary};
    font-weight: 400;
  }
`;

const CloseSearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: ${darkBlue.textTertiary};
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  
  &:hover {
    color: ${darkBlue.textPrimary};
    background: ${darkBlue.iconBg};
  }
  
  svg {
    width: 13px;
    height: 13px;
  }
`;

const NavList = styled.nav`
  flex: 1;
  padding: ${({ $isMobile }) => ($isMobile ? '1rem 0' : '0.75rem 0')};
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  /* Always show a thin scrollbar on touch devices */
  @media (hover: none) and (pointer: coarse) {
    scrollbar-width: thin;
    scrollbar-color: ${darkBlue.textTertiary} ${darkBlue.border};

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: ${darkBlue.border};
    }
    &::-webkit-scrollbar-thumb {
      background: ${darkBlue.textTertiary};
      border-radius: 3px;
    }
  }

  /* Non‑touch: hidden until hover/focus */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
  &:hover, &:focus-within {
    scrollbar-width: thin;
    scrollbar-color: ${darkBlue.textTertiary} ${darkBlue.border};
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: ${darkBlue.border};
    }
    &::-webkit-scrollbar-thumb {
      background: ${darkBlue.textTertiary};
      &:hover {
        background: ${darkBlue.textSecondary};
      }
    }
  }
`;

const NavItem = styled.div`
  margin-bottom: ${({ $isMobile, $sidebarState }) => {
    if ($isMobile) return '0.25rem';
    return $sidebarState === 'collapsed' ? '0.375rem' : '0.125rem';
  }};
  position: relative;
`;

const StyledNavLink = styled(NavLink)`
  width: 100%;
  padding: ${({ $sidebarState, $isMobile }) => {
    if ($isMobile) return '1rem 1.25rem';
    return $sidebarState === 'collapsed' ? '0.45rem 0.2rem' : '0.75rem 1rem';
  }};
  border-radius: 0;
  background: transparent;
  color: ${darkBlue.textSecondary};
  display: flex;
  align-items: center;
  justify-content: ${({ $sidebarState }) =>
    $sidebarState === 'collapsed' ? 'center' : 'flex-start'};
  gap: ${({ $isMobile, $sidebarState }) => {
    if ($isMobile) return '0.75rem';
    return $sidebarState === 'collapsed' ? '0px' : '0.5rem';
  }};
  text-align: left;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  text-decoration: none;
  height: ${({ $isMobile, $sidebarState }) => {
    if ($isMobile) return '52px';
    return $sidebarState === 'collapsed' ? 'auto' : '44px';
  }};
  min-height: ${({ $sidebarState, $isMobile }) =>
    !$isMobile && $sidebarState === 'collapsed' ? '50px' : 'auto'};
  flex-direction: ${({ $sidebarState, $isMobile }) =>
    !$isMobile && $sidebarState === 'collapsed' ? 'column' : 'row'};
  align-items: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${darkBlue.textPrimary};
    border-left-color: ${darkBlue.accent};
    
    .nav-icon {
      background: ${darkBlue.iconHoverBg};
      color: ${darkBlue.accent};
      transform: translateY(-1px);
    }
  }
  
  &.active {
    background: rgba(59, 130, 246, 0.15);
    border-left-color: ${darkBlue.accent};
    color: ${darkBlue.textPrimary};
    
    .nav-icon {
      background: ${darkBlue.iconActiveBg};
      color: ${darkBlue.accent};
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
    }
  }
  
  .nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ $isMobile, $sidebarState }) => {
      if ($isMobile) return '36px';
      return $sidebarState === 'collapsed' ? 'auto' : '28px';
    }};
    height: ${({ $isMobile, $sidebarState }) => {
      if ($isMobile) return '36px';
      return $sidebarState === 'collapsed' ? 'auto' : '28px';
    }};
    border-radius: ${({ $isMobile, $sidebarState }) => {
      if ($isMobile) return '8px';
      return $sidebarState === 'collapsed' ? '6px' : '6px';
    }};
    background: ${({ $sidebarState }) =>
      $sidebarState === 'collapsed'
        ? 'transparent'
        : darkBlue.iconBg};
    color: ${darkBlue.textTertiary};
    flex-shrink: 0;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    
    svg {
      width: ${({ $isMobile, $sidebarState }) => {
        if ($isMobile) return '16px';
        return $sidebarState === 'collapsed' ? '14px' : '14px';
      }};
      height: ${({ $isMobile, $sidebarState }) => {
        if ($isMobile) return '16px';
        return $sidebarState === 'collapsed' ? '14px' : '14px';
      }};
    }
  }
  
  .nav-content {
    opacity: ${({ $sidebarState, $isMobile }) => {
      if ($isMobile) return '1';
      return $sidebarState === 'collapsed' ? '0' : '1';
    }};
    transition: opacity 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    flex: 1;
  }
  
  .nav-title {
    display: block;
    font-size: ${({ $isMobile }) =>
      $isMobile ? '0.9375rem' : '0.8125rem'};
    font-weight: 400;
    color: inherit;
    line-height: 1.2;
  }

  .nav-full {
    font-size: 8px;
    font-weight: 500;
    margin-top: -10px;
    color: ${darkBlue.textTertiary};
    text-align: center;
    line-height: 1;
    max-width: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &[data-tooltip] {
    position: relative;
  }

  &[data-tooltip]:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 8px;
    padding: 4px 8px;
    background: ${darkBlue.tooltipBg};
    color: ${darkBlue.textPrimary};
    font-size: 12px;
    font-weight: 400;
    white-space: nowrap;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    z-index: 1001;
    pointer-events: none;
    animation: fadeIn 0.15s ease;
  }

  &[data-tooltip]:hover::before {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 0;
    border-width: 5px;
    border-style: solid;
    border-color: transparent ${darkBlue.tooltipBg} transparent transparent;
    z-index: 1001;
    pointer-events: none;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-50%) translateX(-5px); }
    to { opacity: 1; transform: translateY(-50%) translateX(0); }
  }
`;

const SidebarFooter = styled.div`
  padding: ${({ $isMobile }) =>
    $isMobile ? '1.5rem' : '0.875rem'};
  border-top: 1px solid ${darkBlue.border};
  display: ${({ $sidebarState, $isMobile }) => {
    if ($isMobile) return 'block';
    return $sidebarState === 'collapsed' ? 'none' : 'block';
  }};
  
  small {
    color: ${darkBlue.textTertiary};
    font-size: ${({ $isMobile }) =>
      $isMobile ? '0.8125rem' : '0.6875rem'};
    line-height: 1.4;
    display: block;
    font-weight: 400;
    text-align: center;
  }
`;

const NoResultsMessage = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: ${darkBlue.textTertiary};
  font-size: 0.8125rem;
  
  p {
    margin: 0.5rem 0;
  }
  
  .highlight {
    color: ${darkBlue.textPrimary};
    font-weight: 600;
  }
`;

// New mobile close button
const MobileCloseButton = styled.button`
  display: ${({ $isMobile, $sidebarState }) =>
    $isMobile && $sidebarState === 'expanded' ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid ${darkBlue.border};
  color: ${darkBlue.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    background: ${darkBlue.iconBg};
    color: ${darkBlue.textPrimary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// --- Main Component ---
const Sidebar = ({
  sidebarState,
  onToggleSidebar,
  isMobile = false
}) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1); // For keyboard navigation in search results
  const [logoError, setLogoError] = useState({ expanded: false, collapsed: false }); // Track errors per logo

  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const searchButtonRef = useRef(null); // For focus return
  const navListRef = useRef(null); // To scroll selected item into view

  // Memoize filtered sections
  const filteredSections = useMemo(() => {
    if (!searchQuery) return SECTIONS;
    const q = searchQuery.toLowerCase();
    return SECTIONS.filter(s =>
      s.title.toLowerCase().includes(q) || s.path.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(filteredSections.length > 0 ? 0 : -1);
  }, [filteredSections]);

  // Focus search input when activated
  useEffect(() => {
    if (isSearchActive && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchActive]);

  // Return focus to search button when search closes
  useEffect(() => {
    if (!isSearchActive && searchButtonRef.current) {
      searchButtonRef.current.focus();
    }
  }, [isSearchActive]);

  // Keyboard shortcut: "/" to focus search (when not in an input)
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Only if sidebar is expanded and not on mobile (or on mobile if expanded)
      if (sidebarState !== 'expanded') return;
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        e.preventDefault();
        setIsSearchActive(true);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [sidebarState]);

  // Close search when sidebar collapses (but preserve query for later expand)
  useEffect(() => {
    if (sidebarState !== 'expanded' && isSearchActive) {
      setIsSearchActive(false);
      // Do NOT clear searchQuery – it will be restored when expanded again
    }
  }, [sidebarState, isSearchActive]);

  const handleNavLinkClick = useCallback(() => {
    setIsSearchActive(false);
    // Keep searchQuery for next expand
    if (isMobile && sidebarState !== 'hidden') {
      onToggleSidebar('hidden');
    }
  }, [isMobile, sidebarState, onToggleSidebar]);

  const handleSearchClick = useCallback(() => {
    setIsSearchActive(true);
  }, []);

  const handleCloseSearch = useCallback(() => {
    setIsSearchActive(false);
    // Keep searchQuery
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // Keyboard navigation in search results
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleCloseSearch();
    } else if (e.key === 'Enter') {
      if (selectedIndex >= 0 && filteredSections[selectedIndex]) {
        navigate(filteredSections[selectedIndex].path);
        handleCloseSearch();
        if (isMobile) onToggleSidebar('hidden');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < filteredSections.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    }
  }, [filteredSections, selectedIndex, navigate, handleCloseSearch, isMobile, onToggleSidebar]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && navListRef.current) {
      const selectedElement = navListRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Don't show search functionality when sidebar is collapsed
  const shouldShowSearch = sidebarState === 'expanded' && !isMobile;

  // Mobile close handler
  const handleMobileClose = useCallback(() => {
    onToggleSidebar('hidden');
  }, [onToggleSidebar]);

  // Logo fallback handlers
  const handleLogoError = useCallback((type) => {
    setLogoError(prev => ({ ...prev, [type]: true }));
  }, []);

  const renderLogo = (type, src, alt) => {
    const hasError = logoError[type];
    return (
      <LogoImage $sidebarState={sidebarState} $isMobile={isMobile}>
        {!hasError ? (
          <img
            src={src}
            alt={alt}
            onError={() => handleLogoError(type)}
          />
        ) : (
          <div style={{ color: 'white', fontSize: '12px' }}>JT</div>
        )}
      </LogoImage>
    );
  };

  const renderHeader = () => {
    if (sidebarState === 'collapsed') {
      return (
        <LogoContainer $isSearchActive={false} $sidebarState={sidebarState}>
          {renderLogo('collapsed', 'https://i.aidevops.tech/0d_loggo.webp', 'JSON Tools Logo')}
        </LogoContainer>
      );
    }

    return (
      <HeaderContent>
        {!isSearchActive ? (
          <>
            <LogoContainer $isSearchActive={false} $sidebarState={sidebarState}>
              {renderLogo('expanded', 'https://i.aidevops.tech/00_image-removebg-preview.png', 'JSON Tools Logo')}
              <SidebarTitle $sidebarState={sidebarState} $isMobile={isMobile} $isSearchActive={false}>
                JSON Tools
              </SidebarTitle>
            </LogoContainer>

            {shouldShowSearch && (
              <SearchWrapper $isSearchActive={false}>
                <SearchButton
                  ref={searchButtonRef}
                  onClick={handleSearchClick}
                  $sidebarState={sidebarState}
                  $isSearchActive={false}
                  title="Search tools (/)"
                  aria-label="Search tools"
                >
                  <SearchIcon />
                </SearchButton>
              </SearchWrapper>
            )}

            {/* Mobile close button */}
            <MobileCloseButton
              $isMobile={isMobile}
              $sidebarState={sidebarState}
              onClick={handleMobileClose}
              aria-label="Close sidebar"
            >
              <XIcon />
            </MobileCloseButton>
          </>
        ) : (
          <SearchWrapper $isSearchActive={true}>
            <SearchContainer>
              <label htmlFor="sidebar-search" className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden' }}>
                Search tools
              </label>
              <SearchInput
                id="sidebar-search"
                ref={searchInputRef}
                type="text"
                placeholder="Search tools... (↑/↓ to navigate, Enter to go)"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
                spellCheck="false"
                aria-label="Search tools"
              />
              <CloseSearchButton onClick={handleCloseSearch} aria-label="Close search">
                <XIcon />
              </CloseSearchButton>
            </SearchContainer>
          </SearchWrapper>
        )}
      </HeaderContent>
    );
  };

  return (
    <SidebarContainer
      $sidebarState={sidebarState}
      $isMobile={isMobile}
      role="complementary"
      aria-label="Main navigation"
    >
      <SidebarHeader
        $sidebarState={sidebarState}
        $isMobile={isMobile}
        $isSearchActive={isSearchActive}
      >
        {renderHeader()}
      </SidebarHeader>

      <NavList
        ref={navListRef}
        $isMobile={isMobile}
        role="navigation"
        aria-label="Tool sections"
      >
        {searchQuery && filteredSections.length === 0 ? (
          <NoResultsMessage>
            <p>No tools found for</p>
            <p className="highlight">"{searchQuery}"</p>
            <p>Try a different search term</p>
          </NoResultsMessage>
        ) : (
          filteredSections.map((section, index) => (
            <NavItem
              key={section.id}
              $isMobile={isMobile}
              $sidebarState={sidebarState}
              data-index={index}
              aria-selected={index === selectedIndex ? 'true' : 'false'}
            >
              <StyledNavLink
                to={section.path}
                $sidebarState={sidebarState}
                $isMobile={isMobile}
                onClick={handleNavLinkClick}
                title={sidebarState === 'collapsed' ? section.title : undefined}
                data-tooltip={sidebarState === 'collapsed' ? section.title : undefined}
                aria-label={sidebarState === 'collapsed' ? section.title : undefined}
              >
                <div className="nav-icon" aria-hidden="true">{section.icon}</div>
                <div className="nav-content">
                  <span className="nav-title">{section.title}</span>
                </div>
                {sidebarState === 'collapsed' && !isMobile && (
                  <div className="nav-full" aria-hidden="true">{section.full}</div>
                )}
              </StyledNavLink>
            </NavItem>
          ))
        )}
      </NavList>

      <SidebarFooter $sidebarState={sidebarState} $isMobile={isMobile}>
        <small>
          Professional JSON tools suite
          {/* Removed misleading "Tap outside to close" hint */}
        </small>
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default Sidebar;